/**
 * @fileoverview Brand data model with JSDoc types
 * Normalizes brand cartridge data from API
 *
 * Part of Phase 1: Data Boundary Refactoring
 * @version 1.0.0
 */

import { normalizeProducts } from './product.js';

/**
 * @typedef {Object} BrandDNA
 * @property {string[]} values - Brand values
 * @property {string[]} personality - Personality traits
 * @property {string[]} audience - Target audience
 * @property {string[]} colors - Brand colors
 */

/**
 * @typedef {Object} BrandStats
 * @property {number} ideas - Total ideas generated
 * @property {number} images - Total images generated
 * @property {number} sessions - Total sessions
 */

/**
 * @typedef {Object} Brand
 * @property {string} id - UUID
 * @property {string} userId - Owner user UUID
 * @property {string} code - Short brand code (e.g., "BC-NIKE")
 * @property {string} name - Brand name
 * @property {string} tagline - Brand tagline
 * @property {string} description - Brand description
 * @property {string} industry - Industry category
 * @property {string} category - Category code (TECH, F&B, etc.)
 * @property {string} archetype - Brand archetype
 * @property {string} tone - Tone of voice
 * @property {string} shellColor - Shell color theme (slate, white, black, etc.)
 * @property {string|null} logoUrl - Brand logo URL
 * @property {string|null} moodImageUrl - Mood board image URL
 * @property {string|null} visualPrompt - Visual style prompt (V5 DNA)
 * @property {string|null} voicePrompt - Voice/tone prompt (V5 DNA)
 * @property {string[]} colors - Brand colors array
 * @property {BrandDNA} dna - Brand DNA attributes
 * @property {BrandStats} stats - Usage statistics
 * @property {import('./product.js').Product[]} products - Brand products
 * @property {string} createdAt - ISO timestamp
 * @property {string|null} updatedAt - ISO timestamp
 */

/**
 * Creates a normalized Brand object from raw API response
 * @param {Object} raw - Raw API response (snake_case)
 * @returns {Brand|null}
 */
export function normalizeBrand(raw) {
  if (!raw) return null;

  // Handle DNA - might be string or object
  let dna = raw.dna || raw.brand_dna || {};
  if (typeof dna === 'string') {
    try {
      dna = JSON.parse(dna);
    } catch {
      dna = {};
    }
  }

  // Handle colors - might be string or array
  let colors = raw.colors || dna.colors || [];
  if (typeof colors === 'string') {
    try {
      colors = JSON.parse(colors);
    } catch {
      colors = [];
    }
  }

  return {
    id: raw.id,
    userId: raw.user_id || raw.userId || null,
    code: raw.code || `BC-${(raw.name || '???').substring(0, 4).toUpperCase()}`,
    name: raw.name || '',
    tagline: raw.tagline || '',
    description: raw.description || '',

    // Classification
    industry: raw.industry || null,
    category: raw.category || 'OTHER',
    archetype: raw.archetype || null,
    tone: raw.tone || null,

    // Shell/UI customization
    shellColor: raw.shell_color || raw.shellColor || 'slate',

    // Media URLs
    logoUrl: raw.logo_url || raw.logoUrl || null,
    moodImageUrl: raw.mood_image_url || raw.moodImageUrl || null,

    // AI Prompts (V5 DNA)
    visualPrompt: raw.visual_prompt || raw.visualPrompt || null,
    voicePrompt: raw.voice_prompt || raw.voicePrompt || null,

    // Colors
    colors: Array.isArray(colors) ? colors : [],

    // Brand DNA
    dna: {
      values: dna.values || [],
      personality: dna.personality || [],
      audience: dna.audience || dna.target_audience || [],
      colors: dna.colors || dna.brand_colors || colors || []
    },

    // Stats (from API or computed)
    stats: {
      ideas: raw.stats?.ideas || 0,
      images: raw.stats?.images || 0,
      sessions: raw.stats?.sessions || 0
    },

    // Nested products (if included)
    products: raw.products ? normalizeProducts(raw.products) : [],

    // Timestamps
    createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
    updatedAt: raw.updated_at || raw.updatedAt || null
  };
}

/**
 * Normalize array of brands
 * @param {Object[]} rawBrands
 * @returns {Brand[]}
 */
export function normalizeBrands(rawBrands) {
  if (!Array.isArray(rawBrands)) return [];
  return rawBrands.map(normalizeBrand).filter(Boolean);
}

/**
 * Convert normalized Brand back to database format for saving
 * @param {Brand} brand
 * @returns {Object} - snake_case format for API
 */
export function brandToDb(brand) {
  if (!brand) return null;

  return {
    id: brand.id,
    code: brand.code,
    name: brand.name,
    tagline: brand.tagline,
    description: brand.description,
    industry: brand.industry,
    category: brand.category,
    archetype: brand.archetype,
    tone: brand.tone,
    shell_color: brand.shellColor,
    logo_url: brand.logoUrl,
    mood_image_url: brand.moodImageUrl,
    visual_prompt: brand.visualPrompt,
    voice_prompt: brand.voicePrompt,
    colors: brand.colors,
    dna: brand.dna
  };
}
